import { motion } from "framer-motion";
import SectionLabel from "../components/SectionLabel";
import SEO from "../components/SEO";
import Picture from "../components/Picture";
import CTABand from "../components/sections/CTABand";
import { PROCESS_STAGES } from "../data/process";

function Stage({ stage, index }) {
    const isEven = index % 2 === 1;
    return (
        <motion.div
            suppressHydrationWarning
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 py-16 md:py-24"
            data-testid={`process-stage-${stage.number}`}
        >
            {/* Spine connector (desktop) */}
            <div
                aria-hidden="true"
                className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-[rgba(201,168,106,0.2)]"
            />
            <div
                aria-hidden="true"
                className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 items-center justify-center w-14 h-14 rounded-full bg-[#0B0B0C] border border-[#C9A86A]/60 text-[#C9A86A] font-serif text-xl"
            >
                {stage.number}
            </div>

            {/* Image side */}
            <div
                className={`relative overflow-hidden aspect-[5/4] [&>picture]:absolute [&>picture]:inset-0 [&>picture]:block [&>picture]:h-full [&>picture]:w-full ${
                    isEven
                        ? "lg:col-span-5 lg:col-start-8 lg:row-start-1"
                        : "lg:col-span-5 lg:col-start-1 lg:row-start-1"
                }`}
            >
                <Picture
                    slug={stage.image}
                    alt={`${stage.title} — ${stage.lede.slice(0, 50)}…`}
                    sizes="(max-width: 1024px) 100vw, 40vw"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0B0B0C]/40 via-transparent to-[#0B0B0C]/40" />
            </div>

            {/* Copy side */}
            <div
                className={`${
                    isEven
                        ? "lg:col-span-5 lg:col-start-1 lg:row-start-1 lg:pr-8"
                        : "lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:pl-8"
                } flex flex-col justify-center`}
            >
                <div className="lg:hidden eyebrow text-[#C9A86A] mb-3">
                    Stage {stage.number}
                </div>
                <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#F3EFE7] leading-[1.08]">
                    {stage.title}
                </h3>
                <p className="mt-6 font-serif italic text-xl md:text-2xl text-[#C9A86A] leading-[1.4]">
                    {stage.lede}
                </p>
                <p className="mt-6 text-[#9A958A] text-base md:text-[17px] leading-[1.85]">
                    {stage.copy}
                </p>
                <ul className="mt-8 space-y-3">
                    {stage.bullets.map((b) => (
                        <li
                            key={b}
                            className="flex gap-4 text-[#F3EFE7] text-[15px] leading-[1.75]"
                        >
                            <span
                                aria-hidden="true"
                                className="mt-2.5 flex-none h-px w-6 bg-[#C9A86A]/70"
                            />
                            <span>{b}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </motion.div>
    );
}

export default function Process() {
    return (
        <div data-testid="process-page">
            <SEO
                title="Our Process — From Discovery to Concierge Stewardship"
                description="A considered four-stage method — Discovery, Design, Installation, and Concierge — walked through with principals and design teams before a cable is pulled."
                canonical="https://actluxe.net/process"
            />
            {/* Hero band */}
            <section className="relative bg-[#0B0B0C] pt-20 md:pt-28 pb-16 md:pb-24 border-b border-[rgba(201,168,106,0.12)] overflow-hidden">
                <div className="shell">
                    <SectionLabel number="Process" label="Our Method" />
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mt-8 items-end">
                        <h1 className="lg:col-span-8 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
                            {"A considered process. "}
                            <span className="italic text-[#C9A86A]">
                                From blueprint to symphony.
                            </span>
                        </h1>
                        <p className="lg:col-span-4 text-[#9A958A] text-base md:text-lg leading-[1.85] max-w-md lg:pb-2">
                            Four stages, written down, walked through, and
                            signed before any cable is pulled. Nothing
                            improvised. Nothing left to discover on site.
                        </p>
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section className="bg-[#0B0B0C] overflow-hidden">
                <div className="shell">
                    {PROCESS_STAGES.map((s, i) => (
                        <div
                            key={s.number}
                            className={`${
                                i !== PROCESS_STAGES.length - 1
                                    ? "border-b border-[rgba(201,168,106,0.12)]"
                                    : ""
                            }`}
                        >
                            <Stage stage={s} index={i} />
                        </div>
                    ))}
                </div>
            </section>

            <CTABand />
        </div>
    );
}
